import { EMPTY_STRING } from "../../constants";
import {
  payrilliumCapturePayment,
  payrilliumGetRequest,
  payrilliumPostRequest,
} from "../../services/payrillium.service";
import { postRequest } from "../../system/serverCall";

const PAYRILLIUM_LOG_VENDOR = "payrillium";

const logPayrilliumAction = (storeId, action, data = {}) => {
  if (!storeId) return;
  postRequest(
    `store-misc-data/${storeId}/${PAYRILLIUM_LOG_VENDOR}`,
    {
      miscData: {
        vendorName: PAYRILLIUM_LOG_VENDOR,
        action,
        timestamp: new Date().toISOString(),
        ...data,
      },
      store: { id: storeId },
      type: PAYRILLIUM_LOG_VENDOR,
    },
    true
  ).catch(() => {});
};

const payrilliumAbortTransactionOnFailure = async (error) => {
  await payrilliumAbortTransaction().catch(() => {});
  throw error;
};

// --------------- Local / Config ---------------

export const getDesktopConfig = (terminal = "", terminalIp = "") =>
  payrilliumGetRequest("local/desktop_config", {
    params: { terminal, terminalIp },
  }).catch(payrilliumAbortTransactionOnFailure);

export const getHostConfig = () =>
  payrilliumGetRequest("local/host_config").catch(
    payrilliumAbortTransactionOnFailure
  );

export const setHostConfig = (config = {}) =>
  payrilliumPostRequest("local/host_config", config).catch(
    payrilliumAbortTransactionOnFailure
  );

export const getMerchantConfig = () =>
  payrilliumGetRequest("local/merchant_config").catch(
    payrilliumAbortTransactionOnFailure
  );

export const setMerchantConfig = (config = {}) => {
  const formData = new FormData();
  Object.entries(config).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      formData.append(key, value);
    }
  });
  return payrilliumPostRequest("local/merchant_config", formData, {
    "Content-Type": "multipart/form-data",
  }).catch(payrilliumAbortTransactionOnFailure);
};

// --------------- Terminal UI ---------------

export const showBasket = (items = [], totalAmount = 0) =>
  payrilliumPostRequest("local/basket", {
    data: {
      items: items.map((item) => ({
        description: item.itemName || item.name || "Item",
        unitPrice: Number(item.actualPrice || item.price || 0),
        quantity: Number(item.quantity || 1),
        unitOfMeasure: "EA",
      })),
      totalAmount: Number(totalAmount).toFixed(2),
      currencyCode: "USD",
    },
  }).catch(payrilliumAbortTransactionOnFailure);

export const getCardType = () =>
  payrilliumGetRequest("local/card_type").catch(
    payrilliumAbortTransactionOnFailure
  );

// --------------- Payment ---------------

export const authorizePayment = ({
  payment_id,
  amount,
  cardType = "CREDIT",
  tip = "0",
  cash_back = "0",
  enableCtlessReading = true,
  enableContactReading = true,
  enableSwipeReading = true,
  enableManualReading = true,
  paymentTimeOut = 60,
  signature,
}) =>
  payrilliumPostRequest("payment/auth", {
    cardType,
    payment_id,
    amount: Number(amount),
    tip,
    cash_back,
    signature,
    enableCtlessReading,
    enableContactReading,
    enableSwipeReading,
    enableManualReading,
    paymentTimeOut,
  }).catch(payrilliumAbortTransactionOnFailure);

export const reverseAuthorization = ({
  payment_id,
  transaction_id,
  totalAmount,
  reason = "CUSTOMER_CANCELLATION",
}) =>
  payrilliumPostRequest("payment/auth_reversal", {
    payment_id,
    transaction_id,
    totalAmount,
    reason,
  }).catch(payrilliumAbortTransactionOnFailure);

export const refundPayment = ({ payment_id, transaction_id, amount }) =>
  payrilliumPostRequest("payment/refund", {
    payment_id,
    transaction_id,
    amount: amount.toString(),
  }).catch(payrilliumAbortTransactionOnFailure);

export const payrilliumAbortTransaction = () =>
  payrilliumPostRequest("payment/abort");

// --------------- Config Initialization ---------------

/**
 * Validates and pushes merchant/host config to the terminal if not already done
 * this session. Reads credentials from the store object.
 */
const initializeTerminalConfig = async (store = {}, paymentDevice = {}) => {
  const {
    merchantID = EMPTY_STRING,
    merchantKeyId = EMPTY_STRING,
    merchantsecretKey = EMPTY_STRING,
    mcc_code = EMPTY_STRING,
  } = store;

  const merchantConfig = await getMerchantConfig();
  if (!merchantConfig?.success || !merchantConfig?.data) {
    if (!merchantID || !merchantKeyId || !merchantsecretKey) {
      throw new Error(
        "Payrillium merchant config is not set. Please configure merchant credentials in Store settings."
      );
    }
    await setMerchantConfig({
      merchantId: merchantID,
      apiKeyId: merchantKeyId,
      secretKey: merchantsecretKey,
      mcc: mcc_code,
      runEnvironment: process.env.REACT_APP_PAYRILLIUM_URL,
    });
  }

  const hostConfig = await getHostConfig();
  if (!hostConfig?.success || !hostConfig?.data) {
    const terminalId = paymentDevice?.poiId || EMPTY_STRING;
    await setHostConfig({
      industryDataType: "NONE",
      demoMode: process.env.REACT_APP_PAYRILLIUM_DEMO_MODE === "true",
      showRemoveCard: true,
      terminalId,
    });
  }
};

// --------------- Transaction Flows ---------------

export const payrilliumProcessSale = async ({
  payment_id,
  amount,
  items = [],
  shouldShowBasket = true,
  cardType = "credit",
  tip = "0",
  paymentTimeOut = 60,
  currentStore = {},
  paymentDevice,
}) => {
  const storeId = currentStore?.id;

  logPayrilliumAction(storeId, "SALE_INITIATED", {
    payment_id,
    amount,
    cardType,
  });

  await initializeTerminalConfig(currentStore, paymentDevice);

  if (shouldShowBasket && items.length > 0) {
    await showBasket(items, amount).catch(() => {});
  }

  const authResponse = await authorizePayment({
    payment_id,
    amount,
    cardType,
    tip,
    paymentTimeOut,
    signature: EMPTY_STRING,
  });

  if (!authResponse?.success) {
    const errorMsg =
      authResponse?.message ||
      (typeof authResponse?.data?.message === "string"
        ? authResponse.data.message
        : null) ||
      "Payment authorization failed";
    logPayrilliumAction(storeId, "AUTH_FAILED", {
      payment_id,
      amount,
      error: errorMsg,
    });
    throw new Error(errorMsg);
  }

  const authData = authResponse?.data?.message;
  const authTransactionId = authData?.transactionId;

  logPayrilliumAction(storeId, "AUTH_SUCCESS", {
    payment_id,
    amount,
    authTransactionId,
    cardVendor: authData?.cardVendor,
    maskedPan: authData?.cardNumber,
    approvalCode: authData?.approvalCode,
    entryMode: authData?.entryMode,
  });

  if (!authTransactionId) {
    logPayrilliumAction(storeId, "AUTH_MISSING_TRANSACTION_ID", {
      payment_id,
      amount,
    });
    throw new Error("Authorization succeeded but no transactionId returned");
  }

  let captureResponse;
  try {
    captureResponse = await payrilliumCapturePayment({
      payment_id,
      transactionId: authTransactionId,
      amount,
      href: authResponse.data?._links?.capture?.href || EMPTY_STRING,
    });
  } catch (captureError) {
    logPayrilliumAction(storeId, "CAPTURE_ERROR", {
      payment_id,
      amount,
      authTransactionId,
      error: captureError?.message,
    });
    await payrilliumAbortTransaction().catch(() => {});
    logPayrilliumAction(storeId, "TRANSACTION_ABORTED", {
      payment_id,
      reason: "CAPTURE_ERROR",
    });
    await reverseAuthorization({
      payment_id,
      transaction_id: authTransactionId,
      totalAmount: amount,
    }).catch(() => {});
    logPayrilliumAction(storeId, "AUTH_REVERSED", {
      payment_id,
      authTransactionId,
      reason: "CAPTURE_ERROR",
    });
    throw new Error(captureError?.message || "Payment capture failed");
  }

  // payrilliumCapturePayment returns a raw axios response; unwrap it
  const captureBody = captureResponse?.data;
  const captureSuccess =
    captureBody?.success ??
    (captureResponse?.status >= 200 && captureResponse?.status < 300);

  if (!captureSuccess) {
    logPayrilliumAction(storeId, "CAPTURE_FAILED", {
      payment_id,
      amount,
      authTransactionId,
      status: captureResponse?.status,
    });
    await payrilliumAbortTransaction().catch(() => {});
    logPayrilliumAction(storeId, "TRANSACTION_ABORTED", {
      payment_id,
      reason: "CAPTURE_FAILED",
    });
    await reverseAuthorization({
      payment_id,
      transaction_id: authTransactionId,
      totalAmount: amount,
    }).catch(() => {});
    logPayrilliumAction(storeId, "AUTH_REVERSED", {
      payment_id,
      authTransactionId,
      reason: "CAPTURE_FAILED",
    });
    throw new Error("Payment capture failed");
  }

  // Support both wrapped { success, data: { message } } and direct response shapes
  const captureData =
    captureBody?.data?.message || captureBody?.message || captureBody;

  logPayrilliumAction(storeId, "CAPTURE_SUCCESS", {
    payment_id,
    amount,
    authTransactionId,
    captureTransactionId: captureData?.id,
  });

  return {
    creditCardData: {
      paymentBrand: authData?.cardVendor || "Credit",
      maskedPan: authData?.cardNumber || "****",
      paymentTokenValue: captureData?.id,
      approvalCode: authData?.approvalCode || "",
      entryMode: authData?.entryMode || "",
    },
    authTransactionId,
    captureTransactionId: captureData?.id,
  };
};

export const payrilliumProcessRefund = async ({
  payment_id,
  transaction_id,
  amount,
  currentStore = {},
}) => {
  const storeId = currentStore?.id;

  logPayrilliumAction(storeId, "REFUND_INITIATED", {
    payment_id,
    transaction_id,
    amount,
  });

  await initializeTerminalConfig(currentStore);

  try {
    const result = await refundPayment({ payment_id, transaction_id, amount });
    logPayrilliumAction(storeId, "REFUND_SUCCESS", {
      payment_id,
      transaction_id,
      amount,
    });
    return result;
  } catch (error) {
    logPayrilliumAction(storeId, "REFUND_FAILED", {
      payment_id,
      transaction_id,
      amount,
      error: error?.message,
    });
    throw error;
  }
};
